import React, {
  createContext,
  useReducer,
  useContext,
  Reducer,
  useCallback,
  PropsWithChildren,
} from 'react';
import { v4 as uuid } from 'uuid';

import { useLockBodyScroll } from 'utils/overlays';

export enum ModalType {
  CONFIRM_MODAL = 'ConfirmModal',
  VIMEO_PLAYER = 'VimeoPlayer',
}

export interface Modal {
  id?: string;
  type: ModalType;
  props?: { [key: string]: any };
  [key: string]: any;
}

export interface ModalContentProps {
  close: () => void;
  closeAllModals?: () => void;
}

interface ModalsState {
  modals: Modal[];
}

interface ModalsAction {
  type: ActionType;
  modal?: Modal;
}

type ActionType = 'openModal' | 'closeModal' | 'closeAllModals';

interface ModalContextValue extends ModalsState {
  ModalType: typeof ModalType;
  openModal: (modal: Modal) => void;
  closeModal: (modal?: Modal) => void;
  closeAllModals: () => void;
}

const initialState: ModalsState = {
  modals: [],
};

const modalsReducer: Reducer<ModalsState, ModalsAction> = (state, action) => {
  const reducerMap = {
    openModal: () =>
      action.modal
        ? {
            modals: [...state.modals, action.modal],
          }
        : state,
    closeModal: () => {
      if (!!action.modal?.id) {
        return {
          modals: state.modals.filter((modal) => modal?.id !== action?.modal?.id),
        };
      }

      return {
        modals: state.modals.filter((modal) => modal?.type !== action?.modal?.type),
      };
    },
    closeAllModals: () => ({
      modals: [],
    }),
  };

  return reducerMap[action?.type] ? reducerMap[action?.type]() : state;
};

export const ModalsContext = createContext<ModalContextValue>({} as ModalContextValue);

/**
 * @deprecated Use /mosaiq/modal instead
 */
export const useModals = () => useContext(ModalsContext);

/**
 * @deprecated Use /mosaiq/modal instead
 */
export const ModalsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(modalsReducer, initialState);
  useLockBodyScroll(!!state.modals?.length);

  const closeAllModals = useCallback(() => {
    dispatch({
      type: 'closeAllModals',
    });
  }, []);

  const closeModal = useCallback(
    (modal?: Modal) => {
      if (!modal) {
        const lastModal = state.modals?.[state.modals?.length - 1];

        dispatch({
          type: 'closeModal',
          modal: lastModal,
        });
        return;
      }

      dispatch({
        type: 'closeModal',
        modal,
      });
    },
    [state.modals],
  );

  const openModal = useCallback((modal: Modal) => {
    const id = uuid();

    dispatch({
      type: 'openModal',
      modal: {
        ...modal,
        id,
      },
    });

    return id;
  }, []);

  return (
    <ModalsContext.Provider
      value={{
        ...state,
        closeAllModals,
        closeModal,
        openModal,
        ModalType,
      }}
    >
      {children}
    </ModalsContext.Provider>
  );
};
